/**
 * Pure helpers for the "arrival_time_text is required when scheduling" rule (#261).
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS:
 * --------------------------------------------------------------------------
 * Same pattern as `dragNDropUpdateFields.js`, `buildScheduleUpdateFields`,
 * and `dateCompare.js`: extract the validation into a pure, importable
 * function so tests exercise the EXACT production code — no test-helper
 * drift (#73, #80, #83, #84). This check guards a mutation that flows to
 * FileMaker via the MongoDB trigger → Hookdeck pipeline, so per CLAUDE.md's
 * Code Review Philosophy + the change-detection/extract rule it gets its own
 * tested helper.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS PREVENTS (#261):
 * --------------------------------------------------------------------------
 * A schedule webhook reached FileMaker with an empty `arrival_time_text`
 * (companyCode KI, JAX, statusCode 4, installer 4033.01, invoiceNo 2539875).
 * Root cause: JobInitModal's `currentArrivalTime` is `useState()` ===
 * `undefined` for a never-touched dropdown, and onSave gated on
 * `currentArrivalTime === ""`. Because `undefined !== ""`, an untouched
 * arrival slipped past validation and a job was scheduled (statusCode 4)
 * with no arrival time. The fix is a robust missing-value check, expressed
 * here once and reused by every status-4 path.
 */

// statusCode that means Scheduled/Assigned — a job placed on the grid with a
// date (see CLAUDE.md "Project Statuses"). This is the state that REQUIRES an
// arrival time. Stored as a float because statusCode can be a string or number
// and sub-statuses exist; compare with parseFloat (Known Pitfalls).
export const SCHEDULED_STATUS_CODE = 4;

/**
 * Does this statusCode represent a scheduled/assigned job (and therefore
 * require an arrival time)? Coerces with parseFloat so "4" and 4 both match.
 *
 * @param {string|number} statusCode
 * @returns {boolean}
 */
export function requiresArrivalTime(statusCode) {
  return parseFloat(statusCode) === SCHEDULED_STATUS_CODE;
}

/**
 * Is an arrival value "missing" for the purpose of the required-field rule?
 * Treats null, undefined, "" and whitespace-only strings all as missing — the
 * robust check that fixes #261 (the old `=== ""` missed undefined).
 *
 * @param {*} arrival
 * @returns {boolean}
 */
export function isArrivalTimeMissing(arrival) {
  return (
    arrival === null ||
    arrival === undefined ||
    String(arrival).trim() === ""
  );
}

/**
 * The combined rule: a job's resulting state violates #261 when it is being
 * scheduled (statusCode 4) but has no arrival time. Returns true when the save
 * should be BLOCKED.
 *
 * @param {Object} params
 * @param {string|number} params.statusCode - the resulting statusCode.
 * @param {*} params.arrival - the resulting arrival_time_text.
 * @returns {boolean} true if arrival is required but missing (block the save).
 */
export function isArrivalRequiredButMissing({ statusCode, arrival }) {
  return requiresArrivalTime(statusCode) && isArrivalTimeMissing(arrival);
}

/**
 * Does a job have a scheduled date? The companion invariant to #261: an
 * `arrival_time_text` is only meaningful for a job that has been scheduled (has
 * a `date_scheduled`). A job in RTS (statusCode 3), Created/Verified
 * (`> 0 && < 3`), or Suppressed (3.1) has no scheduled date.
 *
 * Treats null / undefined / "" / whitespace all as "no scheduled date" — the
 * same robust missing-value semantics as isArrivalTimeMissing.
 *
 * @param {*} dateScheduled - the job's date_scheduled (`start`).
 * @returns {boolean}
 */
export function hasScheduledDate(dateScheduled) {
  return !isArrivalTimeMissing(dateScheduled);
}

/**
 * #263 — may the arrival_time_text be edited / persisted for this job? Only
 * when the job has a scheduled date. Editing/submitting an arrival on an
 * unscheduled job (RTS / Created-Verified / Suppressed) pushes a meaningless
 * value down Scheduler → MongoDB → Hookdeck → FileMaker and corrupts FileMaker.
 *
 * @param {*} dateScheduled - the job's date_scheduled (`start`).
 * @returns {boolean} true when arrival editing is allowed.
 */
export function canEditArrivalTime(dateScheduled) {
  return hasScheduledDate(dateScheduled);
}
